import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommunityService } from '../../../services/community.service';
import { ToastrService } from 'ngx-toastr';
import { CommonModule, Location } from '@angular/common';
import { FreelancerDataService } from '../../../services/freelancer-data.service';
import { NgxPaginationModule } from 'ngx-pagination';
import { DomainService } from '../../domain/domain.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxDocViewerModule } from 'ngx-doc-viewer';

@Component({
  selector: 'app-community-users',
  standalone: true,
  imports: [CommonModule, NgxPaginationModule, NgxDocViewerModule],
  templateUrl: './community-users.component.html',
  styleUrls: ['./community-users.component.css'],
})
export class CommunityUsersComponent implements OnInit {
  communityID!: number;
  slug!: string;

  users: any[] = [];
  communityName = '';
  totalUsers = 0;
  totalPages = 0;

  isLoading = true;
  currentPage = 1;
  limit = 10;

  // NEW: pagination window (10 pages per window)
  pageWindowSize = 10;

  availabiltyDurations: any;
  selectedUser: any;
  resumeViewerUrl: string | null = null;
  @ViewChild('profileModal') profileModal!: TemplateRef<any>;
  userID: any;

  constructor(
    private route: ActivatedRoute,
    private communityService: CommunityService,
    private toastr: ToastrService,
    private dataService: FreelancerDataService,
    public domainService: DomainService,
    private modalService: NgbModal,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.slug = params['slug'];
      this.communityID = +params['communityId'];
      this.loadUsers();
    });
  }

  loadUsers(page: number = 1): void {
    this.isLoading = true;
    this.getConsultantRegistrationDataList();

    this.communityService.getCommunityUsers(this.communityID, {
      page,
      limit: this.limit
    }).subscribe({
      next: (res) => {
        const body = res?.data?.body ?? {};
        const apiUsers: any[] = body.users ?? [];

        // Guard: never render more than limit on the page
        this.users = apiUsers.slice(0, this.limit);

        this.communityName = body.community?.community_name ?? '';
        this.totalUsers = Number(body.pagination?.total ?? this.users.length);

        const serverLimit = Number(body.pagination?.limit ?? this.limit);
        const serverPage  = Number(body.pagination?.page ?? page);
        const totalPagesByServer = Number(body.pagination?.totalPages ?? 0);
        const totalPagesByClient = Math.max(1, Math.ceil(this.totalUsers / serverLimit));

        this.limit = isNaN(serverLimit) || serverLimit <= 0 ? this.limit : serverLimit;
        this.totalPages = totalPagesByServer > 0 ? totalPagesByServer : totalPagesByClient;
        this.currentPage = Math.min(Math.max(1, serverPage), this.totalPages);

        this.isLoading = false;

        // Optional: scroll to top for better UX
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
      },
      error: () => {
        this.toastr.error('Failed to load community users');
        this.isLoading = false;
      }
    });
  }

  /** Basic page change */
  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.loadUsers(page);
  }

  /** ----- Pagination window helpers (10 at a time) ----- */

  /** Index of the current window (0-based), e.g., pages 1-10 => 0, 11-20 => 1 */
  get pageWindowIndex(): number {
    return Math.floor((this.currentPage - 1) / this.pageWindowSize);
  }

  /** The first page number in the current window */
  get windowStart(): number {
    return this.pageWindowIndex * this.pageWindowSize + 1;
  }

  /** The last page number in the current window */
  get windowEnd(): number {
    return Math.min(this.windowStart + this.pageWindowSize - 1, this.totalPages);
  }

  /** The array of page numbers to render for the current window */
  get windowPages(): number[] {
    const pages: number[] = [];
    for (let p = this.windowStart; p <= this.windowEnd; p++) pages.push(p);
    return pages;
  }

  /** Jump to previous/next window of 10 pages */
  gotoPrevWindow(): void {
    const target = this.windowStart - this.pageWindowSize; // first page of the previous window
    if (target >= 1) this.onPageChange(target);
  }

  gotoNextWindow(): void {
    const target = this.windowStart + this.pageWindowSize; // first page of the next window
    if (target <= this.totalPages) this.onPageChange(target);
  }

  /** Jump to first/last window quickly */
  gotoFirstWindow(): void {
    if (this.currentPage !== 1) this.onPageChange(1);
  }

  gotoLastWindow(): void {
    if (this.currentPage !== this.totalPages) this.onPageChange(this.totalPages);
  }

  /** ----- Availability helpers ----- */

  getConsultantRegistrationDataList(): void {
    this.dataService.getconsultantRegistration().subscribe({
      next: (res) => {
        const consultantRegistration = res?.data;
        this.availabiltyDurations = consultantRegistration?.durations || [];
      },
      error: (err) => {
        console.error('Error fetching consultant registration:', err);
      }
    });
  }

  getDurationLabel(id: number | null): string {
    if (!id || id === 8) return ''; // 8 is "Unknown"
    const found = this.availabiltyDurations?.find(
      (d: { duration_id: number }) => d.duration_id === id
    );
    return found ? found.value : '';
  }

  getAvailabilityStatus(av: { start_at: string | null; duration_id: number | null }): string {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const start = av?.start_at ? new Date(av.start_at) : null;
    if (start) start.setHours(0, 0, 0, 0);

    const rawDurationText = av?.duration_id ? this.getDurationLabel(av.duration_id) : '';
    const durationText = rawDurationText && rawDurationText !== 'Unknown' ? rawDurationText : null;

    if (!start || start <= today) {
      return durationText ? `Available Now (${durationText})` : 'Available Now';
    }

    const formattedDate = start.toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
    });

    return durationText
      ? `Available From ${formattedDate} (${durationText})`
      : `Available From ${formattedDate}`;
  }

  getAvailabilityBadgeClass(av: { start_at: string | null; duration_id: number | null }): string {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const startAt = av?.start_at ? new Date(av.start_at) : null;
    if (startAt) startAt.setHours(0, 0, 0, 0);
    return !startAt || startAt <= today ? 'bg-success text-white' : 'bg-warning text-dark';
  }

  /** Showing X–Y of Z */
  get showingFrom(): number {
    if (this.totalUsers === 0) return 0;
    return (this.currentPage - 1) * this.limit + 1;
  }

  get showingTo(): number {
    if (this.totalUsers === 0) return 0;
    const end = this.currentPage * this.limit;
    return end > this.totalUsers ? this.totalUsers : end;
  }

  /** Profile modal */
  openProfile(user: any) {
    this.userID = user.user_id;
    this.communityService.getUserPublicProfile(this.communityID, this.userID).subscribe({
      next: (res) => {
        this.selectedUser = res.data.data;
        this.resumeViewerUrl = this.selectedUser?.resume_url || null;
        this.modalService.open(this.profileModal, {
          size: 'lg',
          backdrop: 'static',
          keyboard: false
        });
      },
      error: () => {
        this.toastr.error('Failed to load user profile.');
      },
    });
  }

  /** TrackBy for rows */
  trackByUserId = (_: number, u: any) => u?.user_id ?? u?.id ?? _;

  goBack(): void {
     this.location.back();
  }
}
